"use client";
import { Routes, Route } from "react-router-dom";
import { useState, useEffect } from "react";

import { AdminLogin } from "./layouts/AdminLogin";
import { AdminLayout } from "./layouts/AdminLayout";
import { CoursesSection } from "./sections/CoursesSection";
import { StudentsSection } from "./sections/StudentsSection";
import { AnnouncementsSection } from "./sections/AnnouncementsSection";
import { ChallengesSection } from "./sections/ChallengesSection";
import { PaymentsSection } from "./sections/PaymentsSection";
import { CourseBuilder } from "./features/course-builder/CourseBuilder";
import { LandingPage } from "./pages/LandingPage";
import { LoginPage } from "./pages/LoginPage";
import { DashboardPage } from "./pages/DashboardPage";
import { RegisterPage } from "./pages/RegisterPage";
import { PaymentPage } from './pages/PaymentPage';
import { LearningPage } from './pages/LearningPage';
import { CertificatePage } from "./pages/CertificatePage";
import AnnouncementsPage from "./pages/AnnouncementsPage";
import MonthlyChallenges from "./pages/MonthlyChallenges";

type Section =
  | "courses"
  | "students"
  | "announcements"
  | "challenges"
  | "payments";

export default function App() {
  const [activeSection, setActiveSection] = useState<Section>("courses");
  const [token, setToken] = useState<string | null>(null);
  const [courseView, setCourseView] = useState<"list" | "builder">("list");
  const [editingCourse, setEditingCourse] = useState<any>(null);

  // ✅ Load token from localStorage
  useEffect(() => {
    const savedToken = localStorage.getItem("adminToken") || localStorage.getItem("token");
    setToken(savedToken);
  }, []);

  const handleLoginSuccess = (newToken: string) => {     // ← Removed unused adminData
    localStorage.setItem("adminToken", newToken);
    setToken(newToken);
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("token");
    setToken(null);
  };

  const handleCreate = () => {
    setEditingCourse(null);
    setCourseView("builder");
  };

  const handleEdit = (course: any) => {
    setEditingCourse(course);
    setCourseView("builder");
  };

  const handleSave = () => {
    setCourseView("list");
    setEditingCourse(null);
  };

  const handleCancel = () => {
    setCourseView("list");
    setEditingCourse(null);
  };

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/dashboard" element={<DashboardPage />} />
      <Route path="/payments" element={<PaymentPage />} />
      <Route path="/course/:courseId" element={<LearningPage />} />
      <Route path="/certificate/:courseId" element={<CertificatePage />} />
      <Route path="/announcements" element={<AnnouncementsPage />} />
      <Route path="/challenges" element={<MonthlyChallenges />} />

      {/* ADMIN ROUTE */}
      <Route
        path="/admin"
        element={
          !token ? (
            <AdminLogin onLoginSuccess={handleLoginSuccess} />
          ) : (
            <AdminLayout
              activeSection={activeSection}
              onSectionChange={setActiveSection}
              onLogout={handleLogout}
            >
              {activeSection === "courses" && (
                <>
                  {courseView === "list" && (
                    <CoursesSection onCreate={handleCreate} onEdit={handleEdit} />
                  )}
                  {courseView === "builder" && (
                    <CourseBuilder
                      course={editingCourse}
                      onSave={handleSave}
                      onCancel={handleCancel}
                    />
                  )}
                </>
              )}
              {activeSection === "students" && <StudentsSection />}
              {activeSection === "announcements" && <AnnouncementsSection />}
              {activeSection === "challenges" && <ChallengesSection />}
              {activeSection === "payments" && <PaymentsSection />}
            </AdminLayout>
          )
        }
      />
    </Routes>
  );
}
